/* eslint-disable no-console */
/* eslint-disable radar/no-duplicate-string */
/* eslint-disable radar/cognitive-complexity */
import { useCallback } from 'react';

import { useQueryClient } from 'react-query';
import { useChannel, useEvent } from '@harelpls/use-pusher';
import { useParams } from 'react-router';

import {
  AskingEvent,
  CallToActionEvent,
  ChatEvent,
  EmojiEvent,
  PollEvent,
  PublicQuestionsEvent,
  QuizEvent,
  WordCloudEvent,
} from '@common/types/pusher/events';
import { ROOM_SETTING_EVENT } from '@constants';
import {
  EmojiStatisticsData,
  PinnedMessageData,
  RoomBroadcastData,
  RoomInteractions,
  StreamingData,
  VisibleInteraction,
} from '@common/types/api';
import { BroadcastPageParams } from '@pages/Broadcast/types';

type InteractionEvents =
  | AskingEvent
  | ChatEvent
  | EmojiEvent
  | PollEvent
  | PublicQuestionsEvent
  | QuizEvent
  | WordCloudEvent
  | CallToActionEvent;

const generateBroadcastChannel = (
  interaction: keyof RoomInteractions,
  roomId?: number,
) => `room.${roomId}.${interaction}`;

export const useBroadcastChannels = (roomId?: number) => {
  const queryClient = useQueryClient();
  const { roomSlug } = useParams<BroadcastPageParams>();

  const chatChannel = useChannel(generateBroadcastChannel('chat', roomId));
  const emojiChannel = useChannel(generateBroadcastChannel('emoji', roomId));
  const pollChannel = useChannel(generateBroadcastChannel('poll', roomId));
  const quizChannel = useChannel(generateBroadcastChannel('quiz', roomId));
  const askingChannel = useChannel(generateBroadcastChannel('asking', roomId));
  const ctaChannel = useChannel(
    generateBroadcastChannel('call_action', roomId),
  );
  const cloudChannel = useChannel(
    generateBroadcastChannel('word_cloud', roomId),
  );
  const sponsorsChannel = useChannel(
    generateBroadcastChannel('sponsors', roomId),
  );
  const bannersChannel = useChannel(
    generateBroadcastChannel('banners', roomId),
  );
  const pinMessageChannel = useChannel(`room.${roomId}.pin_message`);
  const streamingsChannel = useChannel(`room.${roomId}.streamings`);
  const publicQuestionsChannel = useChannel(`room.${roomId}.public-questions`);
  const visibleInteractionsChannel = useChannel(`room.${roomId}.settings`);

  const handleEnableInteractionsEvents = useCallback(
    (response?: InteractionEvents) => {
      if (!response) {
        return;
      }

      const queryKey = ['broadcast', roomSlug];

      const previousCache =
        queryClient.getQueryData<RoomBroadcastData>(queryKey);

      if (previousCache) {
        const updatedSetting = {} as RoomInteractions;

        if ('asking' in response) {
          console.info(
            `Perguntas ${response.asking ? 'disponíveis' : 'desabilitadas'}`,
          );

          Object.assign(updatedSetting, { asking: response.asking });
        } else if ('chat' in response) {
          console.info(`Chat ${response.chat ? 'disponível' : 'desabilitado'}`);

          Object.assign(updatedSetting, { chat: response.chat });
        } else if ('emoji' in response) {
          console.info(
            `Emojis ${response.emoji ? 'disponíveis' : 'desabilitados'}`,
          );

          Object.assign(updatedSetting, { emoji: response.emoji });

          const emojiQueryKey = ['emoji', roomId];

          const previousEmojiCache =
            queryClient.getQueryData<EmojiStatisticsData>(emojiQueryKey);

          queryClient.setQueryData<EmojiStatisticsData>(emojiQueryKey, {
            ...(previousEmojiCache || {}),
            ...response.data,
          });
        } else if ('poll' in response) {
          console.info(
            `Enquetes ${response.poll ? 'disponíveis' : 'desabilitadas'}`,
          );

          Object.assign(updatedSetting, { poll: response.poll });

          queryClient.invalidateQueries(['poll', roomId]);
        } else if ('quiz' in response) {
          console.info(
            `Quiz ${response.quiz ? 'disponíveis' : 'desabilitados'}`,
          );

          Object.assign(updatedSetting, { quiz: response.quiz });

          queryClient.invalidateQueries(['quiz', roomId]);
        } else if ('word_cloud' in response) {
          console.info(
            `Nuvem de palavras ${
              response.word_cloud ? 'disponível' : 'desabilitada'
            }`,
          );

          Object.assign(updatedSetting, { word_cloud: response.word_cloud });

          queryClient.invalidateQueries(['word_cloud', roomId]);
        } else if ('call_action' in response) {
          console.info(
            `CTA ${response.call_action ? 'disponível' : 'desabilitado'}`,
          );

          Object.assign(updatedSetting, { call_action: response.call_action });
          Object.assign(previousCache, {
            callAction: {
              data: response.data,
            },
          });
        } else if ('sponsors' in response) {
          const value = (response as Record<string, unknown>)
            .sponsors as number;
          console.info(
            `Patrocinadores ${value ? 'disponíveis' : 'desabilitados'}`,
          );

          Object.assign(updatedSetting, { sponsors: value });
        } else if ('banners' in response) {
          const bannersResponse = response as Record<string, unknown>;
          const value = bannersResponse.banners as number;
          console.info(`Banners ${value ? 'disponíveis' : 'desabilitados'}`);

          Object.assign(updatedSetting, { banners: value });
          Object.assign(previousCache, {
            banners: { data: bannersResponse.data },
          });
        } else if ('public-questions' in response) {
          const value = response['public-questions'];
          console.info(
            `Perguntas Públicas ${value ? 'disponíveis' : 'desabilitadas'}`,
          );

          // Map 'public-questions' to 'public_questions' for settings
          Object.assign(updatedSetting, { public_questions: value });

          queryClient.invalidateQueries(['public-questions', roomId]);
        }

        queryClient.setQueryData<RoomBroadcastData>(queryKey, {
          ...previousCache,
          settings: {
            data: {
              ...previousCache.settings.data,
              ...updatedSetting,
            },
          },
        });
      }
    },
    [queryClient, roomId, roomSlug],
  );

  const handleStreamingsEvent = useCallback(
    (response?: { streamings: StreamingData[]; type: string }) => {
      if (!response || response.type !== 'streamings') {
        return;
      }

      console.info('Streamings da sala atualizados');

      const queryKey = ['broadcast', roomSlug];
      const previousCache =
        queryClient.getQueryData<RoomBroadcastData>(queryKey);

      if (previousCache) {
        queryClient.setQueryData<RoomBroadcastData>(queryKey, {
          ...previousCache,
          streamings: {
            data: response.streamings,
          },
        });
      }
    },
    [queryClient, roomSlug],
  );

  const handleVisibleInteractionsEvent = useCallback(
    (response?: {
      type: string;
      visible_interactions: VisibleInteraction[];
    }) => {
      if (!response || response.type !== 'settings') {
        return;
      }

      console.info('Configuração de interações visíveis atualizada');

      const queryKey = ['broadcast', roomSlug];
      const previousCache =
        queryClient.getQueryData<RoomBroadcastData>(queryKey);

      if (previousCache) {
        queryClient.setQueryData<RoomBroadcastData>(queryKey, {
          ...previousCache,
          settings: {
            data: {
              ...previousCache.settings.data,
              visible_interactions: response.visible_interactions,
            },
          },
        });
      }
    },
    [queryClient, roomSlug],
  );

  useEvent(chatChannel, ROOM_SETTING_EVENT, handleEnableInteractionsEvents);
  useEvent(emojiChannel, ROOM_SETTING_EVENT, handleEnableInteractionsEvents);
  useEvent(askingChannel, ROOM_SETTING_EVENT, handleEnableInteractionsEvents);
  useEvent(pollChannel, ROOM_SETTING_EVENT, handleEnableInteractionsEvents);
  useEvent(quizChannel, ROOM_SETTING_EVENT, handleEnableInteractionsEvents);
  useEvent(cloudChannel, ROOM_SETTING_EVENT, handleEnableInteractionsEvents);
  useEvent(ctaChannel, ROOM_SETTING_EVENT, handleEnableInteractionsEvents);
  useEvent(sponsorsChannel, ROOM_SETTING_EVENT, handleEnableInteractionsEvents);
  useEvent(bannersChannel, ROOM_SETTING_EVENT, handleEnableInteractionsEvents);
  useEvent(
    publicQuestionsChannel,
    ROOM_SETTING_EVENT,
    handleEnableInteractionsEvents,
  );
  useEvent(streamingsChannel, ROOM_SETTING_EVENT, handleStreamingsEvent);
  useEvent(
    visibleInteractionsChannel,
    ROOM_SETTING_EVENT,
    handleVisibleInteractionsEvent,
  );

  // Pin message via Pusher
  const handlePinMessageEvent = useCallback(
    (response?: { pinned_message: unknown; type: string }) => {
      if (!response || response.type !== 'pin_message') return;

      console.info(
        response.pinned_message ? 'Mensagem fixada' : 'Mensagem desafixada',
      );

      const queryKey = ['broadcast', roomSlug];
      const previousCache =
        queryClient.getQueryData<RoomBroadcastData>(queryKey);

      if (previousCache) {
        queryClient.setQueryData<RoomBroadcastData>(queryKey, {
          ...previousCache,
          pinnedMessage: response.pinned_message
            ? { data: response.pinned_message as PinnedMessageData }
            : null,
        });
      }
    },
    [queryClient, roomSlug],
  );

  useEvent(pinMessageChannel, ROOM_SETTING_EVENT, handlePinMessageEvent);
};
